import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { FuelMix } from "../../../api/grid";

interface GenerationMixChartProps {
  fuels: FuelMix[];
  compact?: boolean;
}

const GenerationMixChart: React.FC<GenerationMixChartProps> = ({ fuels, compact = false }) => {
  if (!fuels.length) {
    return <p className="text-caption text-text-muted">No generation data available</p>;
  }

  const data = fuels.map((f) => ({
    name: f.label,
    value: f.generation_mwh,
    color: f.color,
  }));

  if (compact) {
    // Horizontal stacked bar for side panel
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
      <div>
        <div className="flex h-4 rounded-full overflow-hidden bg-surface-700">
          {data.map((d, i) => (
            <div
              key={i}
              title={`${d.name}: ${d.value.toFixed(0)} MWh (${((d.value / total) * 100).toFixed(1)}%)`}
              style={{
                width: `${(d.value / total) * 100}%`,
                backgroundColor: d.color,
              }}
              className="transition-all"
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5 text-caption text-text-secondary">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
              {d.name}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full bar chart for detail page
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 80, right: 20, top: 5, bottom: 5 }}>
        <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fill: "#cbd5e1", fontSize: 12 }}
          width={75}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 8,
            color: "#e2e8f0",
          }}
          formatter={(value: number) => [`${value.toFixed(0)} MWh`, "Generation"]}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default GenerationMixChart;
